import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Layers, FileText, Users, Microscope, Briefcase, Contact, Globe, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EASE = [0.76, 0, 0.24, 1];

const SLUG_TESTIDS = {
  partners: "portal-card-partners",
  "formulation-master": "portal-card-formulation",
  "dossier-assistant": "portal-card-dossier",
  "clinical-intelligence": "portal-card-clinical-intelligence",
};

const SLUG_ICON = {
  crm: Contact, portfolio: Briefcase, hrms: UserRound, bd: Globe,
  "formulation-master": Layers,
  "dossier-assistant": FileText,
  partners: Users,
  "clinical-intelligence": Microscope,
};

// Portal slug ↔ backing module (used to hide portals a user isn't allowed to
// see once a global admin restricts their `modules` list).
const SLUG_MODULE = {
  crm: "crm",
  portfolio: "portfolio",
  hrms: "hrms",
  partners: "partners",
  "formulation-master": "formulations",
  "dossier-assistant": "dossiers",
  "clinical-intelligence": "clinical-intelligence",
};

function PortalCard({ portal, index }) {
  const Icon = SLUG_ICON[portal.slug] || Layers;
  const isInternal = portal.url && portal.url.startsWith("/");
  const linkProps = isInternal
    ? { href: portal.url }
    : { href: portal.url, target: "_blank", rel: "noopener noreferrer" };
  return (
    <motion.a
      {...linkProps}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE, delay: index * 0.08 }}
      className="card group p-6 md:p-7 hover:border-foreground transition-colors duration-300"
      data-testid={SLUG_TESTIDS[portal.slug] || `portal-card-${portal.slug}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-10 h-10 border border-border flex items-center justify-center text-foreground group-hover:bg-foreground group-hover:text-background transition-colors duration-300">
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <span className="w-9 h-9 border border-border flex items-center justify-center text-muted-foreground group-hover:bg-foreground group-hover:text-background group-hover:border-foreground transition-colors duration-300">
          <ArrowUpRight size={14} strokeWidth={1.75} />
        </span>
      </div>

      <div className="font-mono-meta text-muted-foreground mb-3">
        {portal.accent_label}
      </div>
      <h3 className="font-serif-display text-[28px] md:text-[30px] mb-2">
        {portal.name}
      </h3>
      <p className="text-foreground text-[15px] mb-3">{portal.tagline}</p>
      <p className="text-muted-foreground text-sm leading-relaxed mb-8">
        {portal.description}
      </p>

      <div className="pt-5 border-t border-border flex items-end justify-between">
        <div>
          <div className="font-mono-meta text-muted-foreground mb-1">
            {portal.metric_label}
          </div>
          <div className="font-sans text-[32px] leading-none tabular text-foreground">
            {portal.metric_value}
          </div>
        </div>
        <span className="font-mono-meta text-foreground group-hover:text-foreground">
          Launch →
        </span>
      </div>
    </motion.a>
  );
}

export default function PortalsGrid() {
  const { user } = useAuth();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true); setError(false);
    Promise.all([api.get("/portals"), api.get("/dashboard/metrics").catch(() => null)])
      .then(([portalsRes, metricsRes]) => {
        const list = portalsRes.data;
        // Overlay the real counts from /api/dashboard/metrics.
        const metricByLabel = Object.fromEntries(
          (metricsRes?.data?.metrics || []).map((m) => [m.label, m])
        );
        list.forEach((p) => {
          const m = metricByLabel[p.metric_label];
          if (m) p.metric_value = m.value;
        });
        setPortals(list);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [attempt]);

  // Respect the user's RBAC modules list (admins bypass — they see everything).
  const allowed = user?.modules;
  const visible = user?.role === "admin" || !Array.isArray(allowed)
    ? portals
    : portals.filter((p) => allowed.includes(SLUG_MODULE[p.slug] || p.slug));

  return (
    <section className="mt-14" data-testid="portals-section">
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono-meta text-foreground">
          Your Applications — {visible.length} {visible.length === 1 ? "Portal" : "Portals"}
        </div>
        <span className="hidden md:inline font-mono-meta text-muted-foreground">
          Choose a card to open an application
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
        {loading && (
          <div className="md:col-span-2 xl:col-span-4 font-mono-meta text-muted-foreground py-10 text-center">
            Loading portals…
          </div>
        )}
        {error && <div role="alert">Workspaces could not be loaded. Your access has not changed. <button onClick={() => setAttempt(x => x + 1)}>Retry loading workspaces</button></div>}
        {!loading && !error && visible.length === 0 && (
          <div className="md:col-span-2 xl:col-span-4 border border-border bg-card p-10 text-center font-mono-meta text-muted-foreground">
            No modules assigned. Ask an administrator to grant access.
          </div>
        )}
        {visible.map((p, i) => (
          <PortalCard key={p.slug} portal={p} index={i} />
        ))}
      </div>
    </section>
  );
}
